import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, Col, Container, FormGroup, Input, Label, Row } from 'reactstrap';
import { toast } from 'react-toastify';

import Button, { ButtonVariants } from '@/components/Button/Button';
import LoadingSpinner from '@/components/LoadingSpinner';
import {
  getAutoscalerBufferSizes,
  getReadyGameServers,
  getRestartAllocatedJob,
  startRestartAllocatedGameServers,
  updateAutoscalerBufferSizes,
} from '@/services/dev-tools/dev-tools-service';
import { AutoscalerBufferSizesDto } from '@repo/common-types/src/dto/k8s/autoscaler-buffer.dto';
import { RestartAllocatedJobDto } from '@repo/common-types/src/dto/k8s/restart-allocated.dto';
import { Environment, environment } from '@repo/environment';
import { AxiosError } from 'axios';

const DevToolsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [jobSnapshot, setJobSnapshot] = useState<RestartAllocatedJobDto | null>(null);
  const [initialToRestart, setInitialToRestart] = useState<string[]>([]);
  const [bufferInputs, setBufferInputs] = useState({ table: '', tournamentTable: '' });

  const autoscalerBuffersQuery = useQuery({
    queryKey: ['autoscalerBufferSizes'],
    queryFn: () => getAutoscalerBufferSizes(),
    enabled: environment !== Environment.LOCAL,
  });

  const readyGameServersQuery = useQuery({
    queryKey: ['readyGameServers'],
    queryFn: () => getReadyGameServers(),
    enabled: environment !== Environment.LOCAL,
    refetchInterval: 5000,
  });

  useEffect(() => {
    if (!autoscalerBuffersQuery.data) return;
    setBufferInputs({
      table: String(autoscalerBuffersQuery.data.table),
      tournamentTable: String(autoscalerBuffersQuery.data.tournamentTable),
    });
  }, [autoscalerBuffersQuery.data]);

  const autoscalerBuffersMutation = useMutation({
    mutationFn: (payload: AutoscalerBufferSizesDto) => updateAutoscalerBufferSizes(payload),
    onSuccess: (data) => {
      queryClient.setQueryData(['autoscalerBufferSizes'], data);
      setBufferInputs({ table: String(data.table), tournamentTable: String(data.tournamentTable) });
      toast.success('GameServer buffer sizes updated');
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error.response?.data?.message || 'Failed to update GameServer buffer sizes');
    },
  });

  const restartAllocatedMutation = useMutation({
    mutationFn: () => startRestartAllocatedGameServers(),
    onSuccess: (data) => {
      setJobSnapshot(data.job);
      setInitialToRestart(data.job.toRestart ?? []);
      void queryClient.invalidateQueries({ queryKey: ['restartAllocatedJob'] });
      toast.success('Allocated GameServers restart job queued');
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error.response?.data?.message || 'Failed to restart allocated GameServers');
    },
  });

  const restartAllocatedJobQuery = useQuery({
    queryKey: ['restartAllocatedJob'],
    queryFn: () => getRestartAllocatedJob(),
    enabled: environment !== Environment.LOCAL,
    refetchInterval: (query) => {
      const pending = query.state.data?.pending ?? [];
      return pending.length > 0 ? 2000 : false;
    },
  });

  const job = restartAllocatedJobQuery.data ?? jobSnapshot;
  const pendingSet = new Set(job?.pending ?? []);
  const toRestart = job?.toRestart?.length ? job.toRestart : initialToRestart;

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg={12} className="mb-4">
            <Card className="border-0 shadow-sm">
              <CardHeader className="bg-light border-0">
                <h5 className="mb-0">Dev Tools</h5>
                <small className="text-muted">
                  Operational utilities for engineers. Use with care; actions take immediate effect in the current
                  environment.
                </small>
              </CardHeader>
              {environment !== Environment.LOCAL && (
                <CardBody>
                  <h6 className="fw-semibold mb-3">GameServer Buffer Sizes</h6>
                  <p className="text-muted">
                    Adjust buffer sizes for hot tables (cash) and tournament tables. Changes apply immediately via the
                    Agones autoscaler.
                  </p>
                  <Row className="g-3 align-items-end mb-4">
                    <Col md={4}>
                      <FormGroup className="mb-0">
                        <Label for="buffer-table">Hot table buffer</Label>
                        <Input
                          id="buffer-table"
                          type="number"
                          min={0}
                          step={1}
                          value={bufferInputs.table}
                          onChange={(event) => setBufferInputs((prev) => ({ ...prev, table: event.target.value }))}
                          disabled={autoscalerBuffersQuery.isLoading || autoscalerBuffersMutation.isPending}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup className="mb-0">
                        <Label for="buffer-tournament-table">Tournament table buffer</Label>
                        <Input
                          id="buffer-tournament-table"
                          type="number"
                          min={0}
                          step={1}
                          value={bufferInputs.tournamentTable}
                          onChange={(event) =>
                            setBufferInputs((prev) => ({ ...prev, tournamentTable: event.target.value }))
                          }
                          disabled={autoscalerBuffersQuery.isLoading || autoscalerBuffersMutation.isPending}
                        />
                      </FormGroup>
                    </Col>
                    <Col md={4} className="d-flex align-items-center gap-2">
                      <Button
                        variant={ButtonVariants.primary}
                        onClick={() => {
                          const table = Number(bufferInputs.table);
                          const tournamentTable = Number(bufferInputs.tournamentTable);
                          if (!Number.isFinite(table) || !Number.isFinite(tournamentTable)) {
                            toast.error('Buffer sizes must be valid numbers');
                            return;
                          }
                          if (table < 0 || tournamentTable < 0) {
                            toast.error('Buffer sizes must be 0 or greater');
                            return;
                          }
                          if (!Number.isInteger(table) || !Number.isInteger(tournamentTable)) {
                            toast.error('Buffer sizes must be whole numbers');
                            return;
                          }
                          autoscalerBuffersMutation.mutate({ table, tournamentTable });
                        }}
                        disabled={autoscalerBuffersMutation.isPending || autoscalerBuffersQuery.isLoading}
                      >
                        {autoscalerBuffersMutation.isPending ? <LoadingSpinner /> : 'Apply Buffer Sizes'}
                      </Button>
                      {autoscalerBuffersQuery.isLoading && <LoadingSpinner />}
                    </Col>
                  </Row>

                  <Row className="g-3 mb-4">
                    <Col md={6}>
                      <div className="bg-light h-100 rounded p-3 text-center">
                        <div className="d-flex align-items-center justify-content-center mb-1 gap-2">
                          <span className="fw-medium">Ready table servers</span>
                          {readyGameServersQuery.isLoading && <LoadingSpinner />}
                        </div>
                        {readyGameServersQuery.isError ? (
                          <div className="text-danger small">Failed to load.</div>
                        ) : (
                          <div className="display-6 fw-semibold">
                            {readyGameServersQuery.isLoading ? '—' : (readyGameServersQuery.data?.table ?? 0)}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="bg-light h-100 rounded p-3 text-center">
                        <div className="d-flex align-items-center justify-content-center mb-1 gap-2">
                          <span className="fw-medium">Ready tournament table servers</span>
                          {readyGameServersQuery.isLoading && <LoadingSpinner />}
                        </div>
                        {readyGameServersQuery.isError ? (
                          <div className="text-danger small">Failed to load.</div>
                        ) : (
                          <div className="display-6 fw-semibold">
                            {readyGameServersQuery.isLoading ? '—' : (readyGameServersQuery.data?.tournamentTable ?? 0)}
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>

                  <h6 className="fw-semibold mb-3">Restart Allocated GameServers</h6>
                  <p className="text-muted">
                    Deletes only Allocated Agones pods for table, tournament-table, and tournament fleets after ensuring
                    Ready buffer. URLs in DB are cleared for deleted resources.
                  </p>
                  <div className="d-flex mb-3 gap-2">
                    <Button
                      variant={ButtonVariants.primary}
                      onClick={() => {
                        setInitialToRestart([]);
                        setJobSnapshot(null);
                        queryClient.removeQueries({ queryKey: ['restartAllocatedJob'], exact: true });
                        restartAllocatedMutation.mutate();
                      }}
                      disabled={restartAllocatedMutation.isPending || (job?.pending?.length ?? 0) > 0}
                    >
                      {restartAllocatedMutation.isPending || (job?.pending?.length ?? 0) > 0 ? (
                        <LoadingSpinner />
                      ) : (
                        'Run Restart Orchestration'
                      )}
                    </Button>
                  </div>

                  {toRestart?.length > 0 && (
                    <div className="bg-light mb-3 rounded p-3">
                      <div className="mb-2">
                        <span className="fw-medium">GameServers</span>
                      </div>
                      <ul className="mb-0">
                        {toRestart.map((name) => {
                          const isPending = pendingSet.has(name);
                          const className = isPending ? 'text-warning' : 'text-success';
                          return (
                            <li key={name} className={className}>
                              {name}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </CardBody>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default DevToolsPage;
